import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PublicationTypeE } from 'src/app/models/bookPartLink';
import { FAVORITES_FOLDER_NAME_MAX_LENGTH, FavoritesFolderI } from 'src/app/models/favorites';
import { FavoritesService } from 'src/app/online/favorites/service/favorites.service';

export type BookmarkPublicationAddToFolderDialogDataI =
    | { mode: 'add'; publicationId: number; publicationType: PublicationTypeE; bookId?: number; periodic?: boolean }
    | { mode: 'move'; favoritesFileId: number; currentFolderId?: number };

export enum BookmarkAddTargetE {
    WHOLE_BOOK = 'WHOLE_BOOK',
    SPECIFIC_CHAPTER = 'SPECIFIC_CHAPTER'
}

@Component({
    selector: 'app-bookmark-publication-add-to-folder-dialog',
    templateUrl: './bookmark-publication-add-to-folder-dialog.component.html',
    styleUrls: ['./bookmark-publication-add-to-folder-dialog.component.scss']
})
export class BookmarkPublicationAddToFolderDialogComponent implements OnInit {
    readonly FAVORITES_FOLDER_NAME_MAX_LENGTH = FAVORITES_FOLDER_NAME_MAX_LENGTH;
    readonly BookmarkAddTargetE = BookmarkAddTargetE;

    @ViewChild('newFolderInput') private newFolderInput: ElementRef<HTMLInputElement>;

    folders: FavoritesFolderI[] = [];
    filteredFolders: FavoritesFolderI[] = [];
    foldersLoading = false;
    folderSearchText = '';
    selectedFolderId?: number;

    creatingFolder = false;
    newFolderName = '';
    creatingFolderLoading = false;

    addTarget = BookmarkAddTargetE.SPECIFIC_CHAPTER;

    submitting = false;

    constructor(
        private favoritesService: FavoritesService,
        public dialogRef: MatDialogRef<BookmarkPublicationAddToFolderDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: BookmarkPublicationAddToFolderDialogDataI
    ) {
        dialogRef.addPanelClass('w-600');
        if (data.mode === 'move' && data.currentFolderId) {
            this.selectedFolderId = data.currentFolderId;
        }
    }

    ngOnInit(): void {
        this.fetchFolders();
    }

    get isFormValid(): boolean {
        return !!this.selectedFolderId;
    }

    get canChooseAddTarget(): boolean {
        return (
            this.data.mode === 'add' && this.data.publicationType === PublicationTypeE.BOOKPART && !!this.data.bookId
        );
    }

    get wholeBookTargetLabelKey(): string {
        return this.isPeriodic ? 'favorites.addToFolder.target.wholeJournal' : 'book.saveDialog.target.wholeBook';
    }

    get specificPartTargetLabelKey(): string {
        return this.isPeriodic
            ? 'favorites.addToFolder.target.specificArticle'
            : 'favorites.addToFolder.target.specificChapter';
    }

    private get isPeriodic(): boolean {
        return this.data.mode === 'add' && !!this.data.periodic;
    }

    private get addingWholeBook(): boolean {
        return this.canChooseAddTarget && this.addTarget === BookmarkAddTargetE.WHOLE_BOOK;
    }

    fetchFolders(): void {
        this.foldersLoading = true;
        this.favoritesService.getFolders().subscribe({
            next: folders => {
                this.folders = folders;
                this.foldersLoading = false;
                this.updateFilteredFolders();
            },
            error: () => (this.foldersLoading = false)
        });
    }

    onFolderSearchTextChange(folderSearchText: string): void {
        this.folderSearchText = folderSearchText;
        this.updateFilteredFolders();
    }

    selectFolder(folder: FavoritesFolderI): void {
        this.selectedFolderId = folder.id;
    }

    startCreatingFolder(): void {
        this.creatingFolder = true;
        this.newFolderName = '';
        setTimeout(() => this.newFolderInput?.nativeElement.focus());
    }

    cancelCreateFolder(): void {
        this.creatingFolder = false;
        this.newFolderName = '';
    }

    confirmCreateFolder(): void {
        const name = this.newFolderName?.trim();
        if (!name || this.creatingFolderLoading) {
            return;
        }
        this.creatingFolderLoading = true;
        this.favoritesService.createFolder(name).subscribe({
            next: folder => {
                this.folders = [folder, ...this.folders];
                this.selectedFolderId = folder.id;
                this.creatingFolderLoading = false;
                this.creatingFolder = false;
                this.updateFilteredFolders();
            },
            error: () => (this.creatingFolderLoading = false)
        });
    }

    private updateFilteredFolders(): void {
        const searchText = this.folderSearchText?.trim().toLowerCase() || '';
        this.filteredFolders = this.folders
            .filter(folder => !searchText || folder.name.toLowerCase().includes(searchText))
            .sort((folderA, folderB) => folderA.name.localeCompare(folderB.name));
    }

    onSubmit(): void {
        if (!this.isFormValid || this.submitting) {
            return;
        }
        this.submitting = true;
        const request$ =
            this.data.mode === 'move'
                ? this.favoritesService.moveFavorite(this.data.favoritesFileId, this.selectedFolderId)
                : this.favoritesService.addPublicationToFavorites({
                      publicationId: this.addingWholeBook ? this.data.bookId : this.data.publicationId,
                      publicationType: this.addingWholeBook ? PublicationTypeE.BOOK : this.data.publicationType,
                      favoritesFolderId: this.selectedFolderId
                  });
        request$.subscribe({
            next: () => this.dialogRef.close(true),
            error: () => (this.submitting = false)
        });
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
